import { GetServerSideProps, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import stripUndefined from '../../../utils/stripUndefined'
import { getLayout, Layout as LayoutType } from '../../../graphql/layout'
import {
  NewsListingSectionRecord,
  SiteLocale,
} from '../../../graphql/datoSchema.generated'
import { BaseArticleFragment } from '../../../graphql/articleList.generated'
import Layout from '../../../components/Layout'
import BlockZone from '../../../components/BlockZone'
import blocks from '../../../blocks'
import { getPageTitle } from '../../[[...slugs]]'
import { getAllArticleCategories } from '../../../graphql/articleCategoryList'
import { getArticleList } from '../../../graphql/articleList'
import { getSiteConfig, SiteConfig } from '../../../graphql/siteConfig'
import { getPage, Page } from '../../../graphql/page'
import absolutify from '../../../utils/absolutify'
import stringifyQueryString from '../../../utils/stringifyQueryString'
import PageSeo from '../../../components/PageSeo'
import notEmpty from '../../../utils/notEmpty'
import { getSiteSettings, SiteSettings } from '../../../graphql/siteSettings'

export interface StaticProps {
  siteConfig: SiteConfig
  siteSettings: SiteSettings
  layout: LayoutType
  page: Page
  slug: string
}

interface PrivateStaticProps {
  categories: Awaited<ReturnType<typeof getAllArticleCategories>>
}
// next.js does not allow us to use both getServerSideProps and getStaticProps
// currently. This is coming, so I'll keep the structure in place.
// See https://github.com/vercel/next.js/discussions/11424#discussioncomment-2548409
const getStaticPropsFake: GetStaticProps<
  StaticProps & PrivateStaticProps,
  Record<string, never>
> = async ({ locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const [siteConfig, layout, categories, _site] = await Promise.all([
    getSiteConfig(locale as SiteLocale),
    getLayout(locale as SiteLocale),
    getAllArticleCategories(locale as SiteLocale),
    getSiteSettings(locale as SiteLocale),
  ])

  if (!siteConfig?.newsIndex?.slug) {
    throw new Error(
      'Ran into CMS misconfiguration: siteConfig.newsIndex.slug is missing',
    )
  }

  const page = await getPage(locale as SiteLocale, siteConfig.newsIndex.slug)

  if (!page) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  return {
    props: stripUndefined({
      siteConfig,
      siteSettings: _site,
      layout,
      page,
      slug: siteConfig.newsIndex.slug,
      categories,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

interface ServerProps {
  articles: BaseArticleFragment[]
  currentPage: number
  activeFilter?: string
  categories: string[]
}

export const getServerSideProps: GetServerSideProps<
  ServerProps,
  Record<string, never>
> = async (context) => {
  const staticProps = await getStaticPropsFake(context)
  if ('redirect' in staticProps) {
    return staticProps
  }
  if ('notFound' in staticProps) {
    // We are not allowed to return `revalidate` in this function
    return {
      notFound: true,
    }
  }

  const {
    res,
    query: { page, category, dateSort },
    locale,
  } = context
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const { categories } = staticProps.props

  const categoryFilter = staticProps.props.categories.find(
    ({ title }) => title === category,
  )

  const { articles } = await getArticleList(
    locale as SiteLocale,
    categoryFilter?.id,
    100,
  )

  const newsListingSectionProps = staticProps.props.page.content.find(
    (item) => item.__typename === 'NewsListingSectionRecord',
  ) as NewsListingSectionRecord
  const sortDescending = newsListingSectionProps?.dateDesc

  const sortOrder =
    dateSort === undefined || dateSort === sortDescending ? -1 : 1

  articles.sort((a, b) => {
    return (
      sortOrder *
      (new Date(a.publishDate).getTime() - new Date(b.publishDate).getTime())
    )
  })

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=600',
  )

  return {
    props: stripUndefined({
      ...staticProps.props,
      articles,
      categories: categories.map((item) => item.title).filter(notEmpty),
      activeFilter: categoryFilter?.title || undefined,
      currentPage: Number(page) || 1,
    }),
  }
}

const useCanonical = (slug: string, page: number, filter?: string) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }

  return absolutify(
    filter || page > 1
      ? `/${slug}?${stringifyQueryString(
          stripUndefined({
            filter,
            page: page > 1 ? page : undefined,
          }),
        )}`
      : `/${slug}`,
    locale as SiteLocale,
  )
}

const NewsIndex = ({
  siteConfig,
  siteSettings,
  layout,
  page,
  slug,
  categories,
  articles,
  currentPage,
  activeFilter,
}: StaticProps & ServerProps) => {
  const title = getPageTitle(page, siteSettings.globalSeo)
  const canonical = useCanonical(slug, currentPage, activeFilter)

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'news',
          href: `/${slug}`,
          title: title || '?',
        },
      ]}
    >
      <PageSeo
        page={page}
        seoSettings={siteSettings.globalSeo}
        canonical={canonical}
      />

      <BlockZone
        components={blocks}
        content={page.content}
        data={{
          NewsListingSection: {
            categories,
            articles,
            activeFilter,
            currentPage,
          },
        }}
      />
    </Layout>
  )
}

export default NewsIndex
